/**
 * @fileoverview Hook Data Layer para CustomerOrderItemMC: formatea datos derivados de la orden (icono, fecha, monto).
 * @module useCustomerOrderItemData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 1.0.0
 */
import { useMemo } from 'react';
import moment from 'moment';
import getSymbolFromCurrency from 'currency-symbol-map';
import DeliveryIcon from 'images/orders/delivery.svg';
import PickupIcon from 'images/orders/store.svg';
import { formatMoney } from '../../../../util/numberUtils';

/**
 * @typedef {Object} CustomerOrderItemData
 * @property {string} deliveryMethodIcon - Icono de método de entrega
 * @property {string} createdAt - Fecha formateada
 * @property {string} monto - Monto formateado con símbolo y moneda
 */

/**
 * Hook Data Layer para formateo de datos de CustomerOrderItemMC.
 *
 * - Solo formatea y memoiza datos derivados de la orden.
 * - No expone lógica de UI ni acciones de negocio.
 *
 * @param {Object} order - Objeto de orden
 * @returns {CustomerOrderItemData} Datos derivados y memoizados
 * @see CustomerOrderItemMC
 */
export default function useCustomerOrderItemData(order) {
  const deliveryMethodIcon = useMemo(
    () => order?.delivery_method === 'store_pickup' ? PickupIcon : DeliveryIcon,
    [order?.delivery_method]
  );
  const createdAt = useMemo(
    () => moment(order?.created_at).format('DD/MM/YYYY HH:mm'),
    [order?.created_at]
  );
  const monto = useMemo(
    () => `${getSymbolFromCurrency(order?.currency)} ${formatMoney(order?.total)} ${order?.currency || ''}`,
    [order?.currency, order?.total]
  );
  return { deliveryMethodIcon, createdAt, monto };
}
